import Resource from "../base/resource";
import {MediaTypeEnum} from "./mediaScreenLiterals"
import {Semaphore} from "async-mutex";
let mediaSemaphore = new Semaphore(1);


const loadQueue = function (callback) {
    return mediaSemaphore.runExclusive(async () => {
        await callback();
        wv.mediaCounter++;
    });
};

class MediaScreen extends Resource {
    name;
    resourceId;
    mediaType;
    screenID;
    params;
    ignoreContentAspectRatio;

    constructor(props) {
        super(props);
        this.name = this.getMediaName(props);
        this.resourceId = props.resourceId;
        this.mediaType = props.mediaType;
        this.ignoreContentAspectRatio = props.ignoreContentAspectRatio ? props.ignoreContentAspectRatio : false;

    }

    async load(onLoadCallback, proms) {
        if (wv.effectController.stoppedTrainingId === TrainingTreeController._trainingFeature.getActiveTraining()?.id) {
            return;
        }

        const loadMedia = async () => {
            const params = MediaScreenManager.isThisMediaExists(this.resourceId, this.instanceId);
            if (params) {
                MediaScreenTreeData.addMediaScreenToTree(params.screenID, MediaScreenManager.getMediaScreenFromInstanceId(params.instanceId));
                this.screenID = params.screenID;
                this.params = params;
                return;
            }

            console.log('Loading media screen: ', this.name);

            let mediaData = MediaScreenManager.getMediaData({
                id: this.resourceId
            }, MediaScreenType[this.mediaType]);


            if (!mediaData) {
                return;
            }
            let fileData = {
                id: mediaData.id,
                instanceId: this.instanceId,
                name: this.name,
                link: "",
                updatedAt: mediaData.updatedAt,
                isDefaultInstance: this.id === this.instanceId,
            };
            await this.loadMediaScreen(fileData);
            await this.handleMediaScreenLoad(onLoadCallback);
        };

        proms.push(loadQueue(loadMedia.bind(this)));
    }

    async loadMediaScreen(fileData) {
        const mediaScreenData = {
            selectedPoints: null,
            type: MediaScreenType[this.mediaType],
            selectedFile: fileData,
            instanceId: fileData.instanceId,
            isVisible: false,
            hidden: false,
            addHotspot: false,
            ignoreContentAspectRatio: this.ignoreContentAspectRatio,
            isDefaultInstance: fileData.isDefaultInstance
        };
        for (; ;) {
            if (!imageListUpdated) {
                await sleep(200);
                continue;
            }

            const params = await MediaScreenManager.createNewMediaScreen(mediaScreenData);
            this.screenID = params ? params.screenID : undefined;
            this.params = params;
            return;
        }
    }

    async handleMediaScreenLoad(onLoadCallback) {
        for (; ;) {
            if (!MediaScreenManager.allPresentMediaScreens[this.screenID]) {
                await sleep(200);
                continue;
            }

            onLoadCallback(1);
            return;
        }
    }

    async setState(state, {patchState, appendState}) {
        let self = this;
        let screen = MediaScreenManager.allPresentMediaScreens[this.screenID];
        if (!screen) {
            console.log("MediaScreen named " + this.name + " doesn't exist");
            return;
        }

        if (!isNullOrUndefined(state.priority)) {
            screen.priority = state.priority;
            MediaScreenManager.updateAllMediaScreenRenderOrder();
        }

        if (!isNullOrUndefined(state.visible)) MediaScreenManager.changeScreenVisibility(this.screenID, state.visible);
        if (!isEmptyObject(state)) {
            this.setPositioningData(state, appendState);
            let configData = mediaScreenPositioningController.getMediaConfiguration(this.screenID);
            if (configData.parent) screen.updateParent(configData.parent);

            if (configData.position) screen.position.set(...configData.position);
            if (configData.quaternion) screen.quaternion.set(...configData.quaternion);
            if (configData.scale) screen.scale.set(...configData.scale);
        }

        let hideFrame = false;
        if (!isNullOrUndefined(state.hideFrame)) {
            screen.params.hideFrame = state.hideFrame;
            hideFrame = state.hideFrame;
        }
        if (!isTrainingRunning()) {
            hideFrame = MediaScreenManager.MediaPlaneProperty.hideFrame;
        }
        screen.updateMediaFrameHideStatus(hideFrame);

        if (screen.params.type === MediaScreenType.VIDEO && !isNullOrUndefined(state.currentTime)) {
            screen.setVideoCurrentTime(parseFloat(state.currentTime), true);
        }

        if (!isNullOrUndefined(state.opacity)) screen.setContentOpacity(state.opacity);

        if (screen.params.type === MediaScreenType.PDF && !isNullOrUndefined(state.pageNumber)) {
            if (screen.params.content.pdfPageLoaded || wv.effectController.convertPDFToImages) {
                screen.loadPage(state.pageNumber);
            } else {
                screen.params.content.currentPage = state.pageNumber;
            }
        }
    }

    setPositioningData(data, appendState) {
        let finalData = mediaScreenPositioningController.getMediaScreenData(this.screenID);
        if (!finalData) finalData = {};

        finalData.XRHMD = data?.XRHMD ? data.XRHMD : finalData?.XRHMD;
        finalData["2DandTabletAR"] = data.hasOwnProperty("2DandTabletAR") ? data["2DandTabletAR"] : finalData["2DandTabletAR"];
        finalData.Camera = {
            top: !isNullOrUndefined(data?.Camera.top) ? this.toPercentageString(data.Camera.top) : finalData?.Camera?.top,
            left: !isNullOrUndefined(data?.Camera.left) ? this.toPercentageString(data.Camera.left) : finalData?.Camera?.left,
            right: !isNullOrUndefined(data?.Camera.right) ? this.toPercentageString(data.Camera.right) : finalData?.Camera?.right,
            bottom: !isNullOrUndefined(data?.Camera.bottom) ? this.toPercentageString(data.Camera.bottom) : finalData?.Camera?.bottom,
            height: !isNullOrUndefined(data?.Camera.height) ? this.toPercentageString(data.Camera.height) : finalData?.Camera?.height,
            width: !isNullOrUndefined(data?.Camera.width) ? this.toPercentageString(data.Camera.width) : finalData?.Camera?.width,
        };

        let newConfiguration = {
            position: data?.Scene?.position ? data.Scene.position : finalData?.Scene?.position,
            quaternion: data?.Scene?.quaternion ? data.Scene.quaternion : finalData?.Scene?.quaternion,
            scale: data?.Scene?.scale ? data.Scene.scale : finalData?.Scene?.scale || [1, 1, 1],
        };

        finalData.Scene = newConfiguration;

        mediaScreenPositioningController.registerMediaScreenData(this.screenID, finalData);
    }

    toPercentageString(number) {
        const percentage = Math.min(parseFloat(number) || 0, 100);
        return `${percentage}%`;
    }

    mergeStates(initialState) {
        if (this.states.length === 0) {
            return initialState;
        }
        initialState = this.getDefaultState();

        let finalState = initialState;
        this.states.forEach(state => {
            _.merge(finalState, state);
        });

        this.states = [];
        return finalState;
    }

    getDefaultState() {
        return {
            visible: false,
            hideFrame: false,
            pageNumber: 1,
            currentTime: 0,
            VRMode: MediaScreenMode.SCENE_FIX,
            NonVRMode: MediaScreenMode.SCENE_FIX,
            Camera: {
                top: 0,
                left: undefined,
                right: 0,
                bottom: undefined,
                height: 30,
                width: 30,
            },
            Scene: {
                position: [0, 0, 0],
                quaternion: [0, 0, 0, 1],
                scale: [1, 1, 1],
            },
            opacity: 1,
            priority: 0,
        }
    }

    getMediaName(props) {
        let resource = undefined;
        switch (props.mediaType && props.mediaType.toUpperCase()) {
            case MediaTypeEnum.POSTER:
                resource = MediaScreenManager.getImageDataByID(props.resourceId);
                break;
            case MediaTypeEnum.VIDEO:
                resource = MediaScreenManager.getVideoDataByID(props.resourceId);
                break;
            case MediaTypeEnum.PDF:
                resource = MediaScreenManager.getDocumentDataByID(props.resourceId);
                break;
        }
        return resource ? resource.name : undefined;
    }

    compareStates(startStates, endStates) {
        let transitionStates = [];
        for (let state of startStates) {
            if (!endStates.find(endState => endState.id === state.id)) {
                transitionStates.push({
                    ...state,
                    visible: false
                });
            }
        }

        for (let state of endStates) {
            transitionStates.push({...state});
        }

        return transitionStates;
    }

    decompose(state, appendState, resourceData = []) {
        let actions = [];

        const getMediaType = (id) => {
            return resourceData.find(resource => resource.id === id).mediaType;
        };

        for (let entry of state) {
            const mediaType = getMediaType(entry.id);

            // we need to add className to prevent default pruning while loading timeline
            actions.push({
                startTime: 0,
                type: "MediaScreenShow",
                className: mediaType + "_showClass",
                mediaId: entry.id,
                show: entry.visible || false
            });

            if (mediaType === MediaTypeEnum.VIDEO) {
                actions.push({
                    startTime: 0,
                    type: "MediaScreenPlay",
                    className: mediaType + "_playClass",
                    mediaId: entry.id,
                    play: entry.pageNumber === 1 && (entry.visible || false)
                });
            } else if (mediaType === MediaTypeEnum.PDF) {
                actions.push({
                    startTime: 0,
                    type: "MediaScreenChangePage",
                    className: mediaType + "_changePageClass",
                    mediaId: entry.id,
                    pageNumber: entry.pageNumber
                });
            }

            if (mediaType !== MediaTypeEnum.PDF) {
                delete entry.pageNumber;
            }

            actions.push({
                    startTime: 0,
                    actionId: generateUUID(),
                    type: "LoadState",
                    loadViewpoint: false,
                    patchState: false,
                    appendState: appendState,
                    states: [{resourceStates: [entry]}]
                }
            );
        }

        return actions;
    }
}

export default MediaScreen;
